/**
 * moduleWhatsNewStore — stateful binding of the pure modular What's New logic to the app:
 *  - the registry (`moduleEntries`),
 *  - the per-user seen map in AsyncStorage (key `whatsNewModuleSeen:<userId>`),
 *  - the request channel used by `useModuleWhatsNew`,
 *  - a tiny coordinator so the module modal and the global What's New modal never show together.
 *
 * Like the global What's New (`whatsNewLastSeenVersion`) the seen-state is local to the device;
 * the app has no backend seen-sync to extend.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import moduleEntries from './moduleEntries';
import { getSeenKey, getUnseenEntries, markEntriesSeen, parseSeenState } from './moduleWhatsNewLogic';

/**
 * Registry entries (full list, unfiltered).
 * @returns {Object[]}
 */
export const getModuleEntries = () => moduleEntries;

/**
 * Read one user's seen state. Never throws.
 * @param {string} userId Signed-in user id.
 * @returns {Promise<{ v: number, seen: Object<string, string> }>}
 */
export const readSeenState = async (userId) => {
  if (!userId) {
    return parseSeenState(null);
  }
  try {
    return parseSeenState(await AsyncStorage.getItem(getSeenKey(userId)));
  } catch (_e) {
    return parseSeenState(null);
  }
};

/**
 * Persist entry ids as seen for a user. Never throws (a failed write just re-shows the entry once).
 * @param {string} userId Signed-in user id.
 * @param {string[]} ids Entry ids.
 * @returns {Promise<void>}
 */
export const markSeen = async (userId, ids) => {
  if (!userId || !Array.isArray(ids) || ids.length === 0) {
    return;
  }
  try {
    const next = markEntriesSeen(await readSeenState(userId), ids);

    await AsyncStorage.setItem(getSeenKey(userId), JSON.stringify(next));
  } catch (_e) {
    // Non-fatal.
  }
};

/**
 * Unseen entries for a focused route (watcher) or a module key (hook), for one user.
 *
 * @param {Object} query
 * @param {string} query.userId Signed-in user id (empty => nothing).
 * @param {{name?: string, params?: Object}} [query.route] Focused route.
 * @param {string} [query.module] Module key.
 * @param {boolean} [query.force] Ignore seen-state (manual re-open of a module's live entries).
 * @param {string|number|Date} [query.userCreatedAt] Account creation (baseline for new users).
 * @param {boolean} [query.includeInternal] Viewer is system:admin.
 * @returns {Promise<Object[]>}
 */
export const getUnseenModuleEntries = async ({
  userId, route, module, force = false, userCreatedAt, includeInternal = false,
}) => {
  if (!userId) {
    return [];
  }

  return getUnseenEntries(moduleEntries, {
    route,
    module,
    includeInternal,
    userCreatedAt,
    seenState: force ? null : await readSeenState(userId),
  });
};

// ---- Coordination with the global What's New modal ---------------------------------------------

const coordinator = { globalOpen: false, globalSettled: false, listeners: new Set() };

const emit = () => coordinator.listeners.forEach((listener) => {
  try {
    listener({ globalOpen: coordinator.globalOpen, globalSettled: coordinator.globalSettled });
  } catch (_e) {
    // A faulty subscriber must never break the others.
  }
});

/**
 * Called by WhatsNewModal whenever it becomes visible / hidden.
 * @param {boolean} open Whether the global modal is on screen.
 * @returns {void}
 */
export const setGlobalWhatsNewOpen = (open) => {
  coordinator.globalOpen = Boolean(open);
  emit();
};

/**
 * Called by WhatsNewModal once its one-time upgrade check finished (opened or decided not to).
 * Until then the module modal waits, so the global one always wins the first-launch race.
 * @returns {void}
 */
export const markGlobalWhatsNewSettled = () => {
  coordinator.globalSettled = true;
  emit();
};

/**
 * Snapshot of the coordinator.
 * @returns {{ globalOpen: boolean, globalSettled: boolean }}
 */
export const getCoordinatorState = () => ({
  globalOpen: coordinator.globalOpen,
  globalSettled: coordinator.globalSettled,
});

/**
 * Subscribe to coordinator changes.
 * @param {Function} listener Receives `{ globalOpen, globalSettled }`.
 * @returns {Function} Unsubscribe.
 */
export const subscribeCoordinator = (listener) => {
  coordinator.listeners.add(listener);

  return () => coordinator.listeners.delete(listener);
};

// ---- Request channel for useModuleWhatsNew ------------------------------------------------------

const requestListeners = new Set();

/**
 * Ask the mounted watcher to show a module's entries (used by `useModuleWhatsNew`).
 * @param {string} module Module key.
 * @param {Object} [options]
 * @param {boolean} [options.force=false] Show every live entry of the module, not only unseen ones.
 * @returns {void}
 */
export const requestModuleWhatsNew = (module, { force = false } = {}) => {
  if (!module) {
    return;
  }
  requestListeners.forEach((listener) => {
    try {
      listener({ module, force });
    } catch (_e) {
      // Ignore.
    }
  });
};

/**
 * Register the (single) mount's request handler.
 * @param {Function} listener Receives `{ module, force }`.
 * @returns {Function} Unsubscribe.
 */
export const subscribeRequests = (listener) => {
  requestListeners.add(listener);

  return () => requestListeners.delete(listener);
};
